import "./attributes.scss";

import {
    ATTRIBUTES_OBJECT,
    BOOLEAN_KEYS,
    DATE_KEYS,
    OBJECT_KEYS
} from "./constants";
import { Badge, Button, Collapse, UncontrolledTooltip } from "reactstrap";

import { useRef, useState } from "react";

import Flex from "@components/ui-components/flex";
import { LoaderOverlay } from "@src/@core/components/spinner";
import LongNameFormatter from "@components/formatters/long-name-formatter";
import { STAGE_MAP } from "../../../misc/constants";
import Upsert from "../upsert";
import classnames from "classnames";
import { getStatusBadge } from "@src/utility/helpers/utils";
import size from "lodash/size";
import useDateHandler from "@src/utility/hooks/use-date-handler";
import { useTranslation } from "react-i18next";
import ActionWrapper from "@src/@core/components/ui-components/action-wrapper";

const Attributes = (props) => {
    const [showStageDetails, setShowStageDetails] = useState(false);
    const { details, fetchDetails, handleModal, modalConfig, loading } = props;
    const { dateFormatter } = useDateHandler();
    const { t } = useTranslation();
    const { attributes = {}, segments, healthScores } = details || {};

    const getDisplayValue = (key, value) => {
        if (DATE_KEYS.includes(key)) {
            return value ? dateFormatter({ date: value, formatAs: "MMM DD, YYYY" }) : "N/A";
        }
        if (OBJECT_KEYS.includes(key)) {
            return attributes[key]?.name || "N/A";
        }
        if (BOOLEAN_KEYS.includes(key)) {
            return (
                <Badge color={getStatusBadge(String(value)).color}>
                    {getStatusBadge(String(value)).title}
                </Badge>
            );
        }
        if (key === "stage") {
            return STAGE_MAP[value?.name];
        }
        if (key === "conversion_quota") {
            return value ? `${value}%` : "N/A";
        }
        if (key === "time_taken_to_onboard") {
            return value ? `${value} days` : "N/A";
        }
        return value || "N/A";
    };
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    const toggleDrawer = () => {
        setIsOpen((prev) => !prev);
    };
    return (
        <>
            <div
                className={classnames("attribute-toggle-wrapper", {
                    open: isOpen
                })}>
                <Button
                    color=""
                    className="attribute-button custom-btn"
                    onClick={toggleDrawer}>
                    <i className="mdi mdi-menu-open" />
                </Button>
            </div>
            <div
                className={classnames({ show: isOpen })}
                onClick={toggleDrawer}></div>
            <div
                ref={wrapperRef}
                className={classnames("attribute-modal", {
                    open: isOpen
                })}>
                {/* Filter header */}
                <div className="attribute-header">
                    <Flex className="w-100" justify="between" align="center">
                        <h4 className="mb-0 fw-bolder" data-cy="filter-title">
                            {t("Company Attributes")}
                            <ActionWrapper
                                subject="companies"
                                action="update"
                                child={
                                    <span
                                        className="text-underline ms-1 text-primary cursor-pointer"
                                        onClick={() => handleModal(attributes)}>
                                        {t("Edit")}
                                    </span>
                                }/>
                        </h4>
                    </Flex>
                    <div
                        data-cy="filter-modal-close"
                        className="cursor-pointer btn-icon btn d-flex align-items-center text-secondary">
                        <i className="mdi mdi-close font-medium-5" onClick={toggleDrawer} />
                    </div>
                </div>
                <LoaderOverlay
                    active={loading}
                    children={
                        <div className="attribute-body">
                            <div className="content-body px-1 overflow-scroll h-100">
                                <Flex className="border p-1" justify="between" align="center">
                                    <h4 className="d-flex align-items-center">
                                        <i className="mdi mdi-heart-pulse me-1" />
                                        {t("Global Health Score")}
                                    </h4>
                                    <h5 className="ms-1">
                                        {healthScores?.globalHealthScore || "N/A"}
                                    </h5>
                                </Flex>

                                {Object.keys(ATTRIBUTES_OBJECT).map((key) => {
                                    const attributeInfo = ATTRIBUTES_OBJECT[key];
                                    const value = attributes[key];
                                    const displayValue = getDisplayValue(key, value);

                                    return (
                                        <Flex
                                            className="border p-1"
                                            key={key}
                                            justify="between"
                                            align="center">
                                            <h4 className="d-flex align-items-center">
                                                <i className={`mdi mdi-${attributeInfo.icon} me-1`} />
                                                {attributeInfo.label}

                                                {key === "time_taken_to_onboard" && (
                                                    <>
                                                        <i
                                                            id="onboard-info"
                                                            className="mdi mdi-information-outline text-primary cursor-pointer"/>
                                                        <UncontrolledTooltip
                                                            placement="top"
                                                            target="onboard-info">
                                                            {t("Days from Trial to Onboarding Won")}
                                                        </UncontrolledTooltip>
                                                    </>
                                                )}
                                            </h4>
                                            <h5 className="">{displayValue}</h5>
                                        </Flex>
                                    );
                                })}

                                {size(attributes.stage_history) ? (
                                    <div className="mt-1 border-top pt-1">
                                        <Flex
                                            className="cursor-pointer justify-content-between"
                                            onClick={() => setShowStageDetails((prev) => !prev)}>
                                            <h3>{t("Time taken in each stage")}</h3>
                                            <h1 className="fs-3">
                                                <i
                                                    className={`mdi ${showStageDetails ? "mdi-chevron-up" : "mdi-chevron-down"}`}/>
                                            </h1>
                                        </Flex>
                                        <Collapse isOpen={showStageDetails}>
                                            <div className="border ">
                                                {attributes.stage_history.map((stage, index) => (
                                                    <Flex
                                                        key={index}
                                                        justify="between"
                                                        align="center"
                                                        className="border-bottom py-1 px-1">
                                                        <span className="fw-bold">{stage.value}</span>
                                                        <span>
                                                            {stage.daysInStage} {t("days")}
                                                        </span>
                                                    </Flex>
                                                ))}
                                            </div>
                                        </Collapse>
                                    </div>
                                ) : null}
                                {size(attributes.custom_attributes) ? (
                                    <div>
                                        <h3 className="mt-1 border-1 border-top pt-1 pb-1 border-bottom">
                                            {t("Custom Attributes")}
                                        </h3>
                                        {Object.entries(attributes.custom_attributes).map(
                                            ([key, value]) => (
                                                <Flex
                                                    key={key}
                                                    justify="between"
                                                    align="center"
                                                    className="border p-1">
                                                    <LongNameFormatter
                                                        content={key.replace(/_/g, " ")}
                                                        max={30}
                                                        id={key}
                                                        className="text-truncate fw-bold text-capitalize"/>
                                                    <h6>
                                                        {(() => {
                                                            if (typeof value === "boolean") return t(value ? "True" : "False");
                                                            if (value) return value;
                                                            return "N/A";
                                                        })()}
                                                    </h6>
                                                </Flex>
                                            )
                                        )}
                                    </div>
                                ) : null}

                                {size(attributes.calculated_metrics) ? (
                                    <div>
                                        <h3 className="mt-1 border-1 border-top pt-1 pb-1 border-bottom">
                                            {t("Calculated Metrics")}
                                        </h3>
                                        {attributes.calculated_metrics.map((metric) => {
                                            return (
                                                <Flex
                                                    key={metric.name}
                                                    justify="between"
                                                    align="center"
                                                    className="border p-1">
                                                    <LongNameFormatter
                                                        content={metric.name}
                                                        max={30}
                                                        id={metric.name}
                                                        className="text-truncate"/>
                                                    <h6>{metric.value}</h6>
                                                </Flex>
                                            );
                                        })}
                                    </div>
                                ) : null}

                                {size(segments) ? (
                                    <div>
                                        <h3 className="mt-1 border-1 border-top pt-1 pb-1 border-bottom">
                                            {t("Client Segments")}
                                        </h3>
                                        {segments.map((segment) => {
                                            return (
                                                <Flex
                                                    key={segment._id}
                                                    justify="between"
                                                    align="center"
                                                    className="border p-1">
                                                    <div>
                                                        <LongNameFormatter
                                                            content={segment.name}
                                                            max={30}
                                                            id={segment.name}
                                                            className="text-truncate"/>
                                                    </div>
                                                    <Badge
                                                        color={getStatusBadge(segment.status)?.color}
                                                        className="badge-sm">
                                                        {getStatusBadge(segment.status)?.title}
                                                    </Badge>
                                                </Flex>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    }/>
            </div>
            <Upsert
                getCompanies={fetchDetails}
                handleModal={handleModal}
                modalConfig={modalConfig}/>
        </>
    );
};
export default Attributes;
